import { useEffect, useState } from "react";
import { VisitCounterService } from "../../services/VisitCounterService";
import { PersistantStorage } from "../../Utils/LocalStorage";
import { useDisplayMode } from "../context/DisplayModeContext";

// Once per tab: sessionStorage survives reloads but not new tabs. Key mirrors the backend's expected name.
const VISIT_FLAG_KEY = "dfs_visit_counted";

// 10s localStorage cache so the total survives F5 and rapid reloads don't hammer _total.
const CACHE_KEY = "rewampVisitTotal";
const CACHE_TTL_SECONDS = 10;

// Returns the footer's visit total, or null while unresolved / on failure.
export const useVisitCounter = () => {
  const { standalone } = useDisplayMode();
  const [total, setTotal] = useState(() => PersistantStorage.get(CACHE_KEY));

  useEffect(() => {
    // Footer is hidden in standalone; skip network calls there.
    if (standalone) return undefined;

    let cancelled = false;
    const cacheAndSet = (count) => {
      if (count == null) return;
      PersistantStorage.set(CACHE_KEY, count, CACHE_TTL_SECONDS);
      if (!cancelled) setTotal(count);
    };

    (async () => {
      const isNewSession = !sessionStorage.getItem(VISIT_FLAG_KEY);

      if (isNewSession) {
        // Record the visit. _count returns the fresh total, which also busts
        // any stale cache the previous session may have left behind.
        const recorded = await VisitCounterService.recordVisit({});
        if (recorded != null) {
          sessionStorage.setItem(VISIT_FLAG_KEY, "1");
          cacheAndSet(recorded);
          return;
        }
        // Increment failed: leave the flag unset so the next load retries, and
        // fall through to the read path to still surface a count.
      }

      // Read path with the 10s cache.
      const cached = PersistantStorage.get(CACHE_KEY);
      if (cached != null) {
        if (!cancelled) setTotal(cached);
        return;
      }
      cacheAndSet(await VisitCounterService.getTotalVisits({}));
    })();

    return () => {
      cancelled = true;
    };
  }, [standalone]);

  return total;
};
